import { createClient } from '@supabase/supabase-js';
import {
  Project,
  ReportData,
  StoreBreakdown,
  DetailedPhoto,
  ProjectIssue,
  WarehouseAvailability,
  UserProfile,
  ClientPreference,
  ProjectSummary,
  RawPhoto
} from '../../types';

export const STANDARD_WAREHOUSES = ['Jhb', 'CPT', 'KZN', 'PE', 'EL'];

// Define local Answer interface since it's not exported from types.ts
interface Answer {
  call_id: number;
  question_id: number;
  value?: any;
  picture?: string;
  text?: string;
  number?: any;
  choices?: any;
  yes_no?: boolean;
  datetime?: string;
}

export const supabaseUrl = process.env.VITE_SUPABASE_URL || '';
export const supabaseKey = process.env.VITE_SUPABASE_ANON_KEY || '';
export const IMAGE_BASE_URL = process.env.VITE_IMAGE_BASE_URL || '';
export const SIGNATURE_BASE_URL = process.env.VITE_SIGNATURE_BASE_URL || '';

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.localStorage
  }
});

// Auth request cache to prevent duplicate in-flight requests
let userProfilePromise: Promise<UserProfile | null> | null = null;
let lastFetchTime = 0;
const CACHE_TTL = 10000; // 10 seconds

// Generic Query Cache for metadata and static-ish data
const requestCache = new Map<string, { promise: Promise<any>, timestamp: number }>();
const DEFAULT_TTL = 30000; // 30 seconds

// Invalidate all caches — call after user preferences change
export function invalidateAllCaches() {
  requestCache.clear();
  userProfilePromise = null;
  lastFetchTime = 0;
  console.log('[Cache] All caches invalidated');
}

// Invalidate only user-profile and project-metadata caches
export function invalidateUserAndProjectCaches() {
  // Clear user profile cache
  requestCache.delete('user_profile');
  userProfilePromise = null;
  lastFetchTime = 0;
  // Clear all project metadata caches (they are keyed by projects_meta_*)
  for (const key of requestCache.keys()) {
    if (key.startsWith('projects_meta_')) {
      requestCache.delete(key);
    }
  }
  console.log('[Cache] User profile and project metadata caches invalidated');
}

// Helper to ensure JSON columns are parsed as arrays
function ensureArray(val: any): any[] {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('Failed to parse JSON column as array:', e);
      return [];
    }
  }
  return [];
}

async function withCache<T>(key: string, fetcher: () => Promise<T>, ttl = DEFAULT_TTL): Promise<T> {
  const now = Date.now();
  const cached = requestCache.get(key);
  if (cached && (now - cached.timestamp < ttl)) {
    return cached.promise;
  }
  const promise = fetcher().catch(err => {
    requestCache.delete(key);
    throw err;
  });
  requestCache.set(key, { promise, timestamp: now });
  return promise;
}

export async function getCurrentUserProfile(): Promise<UserProfile | null> {
  return withCache('user_profile', async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data: profile, error } = await supabase.from('profiles').select('*').eq('id', user.id).single();
      if (error) return null;
      return profile as UserProfile;
    } catch (e) {
      return null;
    }
  }, 10000); // 10s TTL for auth
}

export async function getAllUsers(): Promise<UserProfile[]> {
  const { data } = await supabase.from('profiles').select('*');
  return data || [];
}

export async function getAllClientNames(): Promise<string[]> {
  const { data } = await supabase.from('projects').select('description').not('description', 'is', null);
  const names = (data?.map(d => d.description as string) || []) as string[];
  return [...new Set(names)];
}

export async function getAllClients() {
  return withCache('all_clients', async () => {
    const { data, error } = await supabase
      .from('clients')
      .select('*, associated_branches')
      .order('name', { ascending: true });
    return { data: data || [], error };
  });
}

export async function addNewClient(name: string, branches: any[] = []) {
  const { data, error } = await supabase
    .from('clients')
    .insert([{ name, associated_branches: branches }])
    .select()
    .single();
  return { data, error };
}

export async function updateClient(id: string, updates: { name?: string; associated_branches?: any[] }) {
  const { data, error } = await supabase
    .from('clients')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

export async function getAllWarehouses() {
  return withCache('all_warehouses', async () => {
    const { data, error } = await supabase
      .from('warehouses')
      .select('*')
      .order('name', { ascending: true });
    return { data: data || [], error };
  });
}

// 1. Fetch only basic project metadata for immediate UI rendering
export async function fetchProjectsMetadata(userProfile?: UserProfile | null, offset = 0, limit = 50, archived: boolean | string = false, search = ''): Promise<{ projects: any[], hasMore: boolean }> {
  const cacheKey = `projects_meta_${archived}_${offset}_${limit}_${userProfile?.id || 'anon'}_${search}`;

  return withCache(cacheKey, async () => {
    let query = supabase.from('projects').select('*');
    if (archived === true) query = query.eq('project_hidden', true);
    else if (archived === false) query = query.or('project_hidden.is.null,project_hidden.eq.false');
    // If archived is anything else (like 'all'), no filter is applied, fetching all projects.
    if (search) {
      query = query.ilike('name', `%${search}%`);
    }

    query = query.order('created_at', { ascending: false }).range(offset, offset + limit);
    if (userProfile?.linked_clients?.length) query = query.in('description', userProfile.linked_clients);

    const { data: projectsChunk, error: projError } = await query;
    if (projError || !projectsChunk || projectsChunk.length === 0) return { projects: [], hasMore: false };

    const hasMore = projectsChunk.length > limit;
    const finalProjects = hasMore ? projectsChunk.slice(0, limit) : projectsChunk;

    return {
      projects: finalProjects.map(p => ({
        ...p,
        warehouse_availability: ensureArray(p.warehouse_availability),
        project_issues: ensureArray(p.project_issues),
        stats: p.stats || { completed: 0, totalCalls: 0, issues: 0, totalPerc: '0' },
        isStatsLoading: !p.stats // If we have cached stats, don't show as loading
      })),
      hasMore
    };
  });
}

// 2. Fetch heavy statistics for a given set of projects
export async function fetchProjectsStats(projects: any[]): Promise<ProjectSummary[]> {
  try {
    if (!projects || projects.length === 0) return [];
    const finalIds = projects.map(p => p.project_id);

    const fetchAllPaginatedForIds = async (table: string, select: string, ids: number[]) => {
      let results: any[] = [];
      const pageSize = 1000;
      let from = 0;
      let hasMoreData = true;
      while (hasMoreData) {
        const { data, error } = await supabase.from(table).select(select).in('project_id', ids).range(from, from + pageSize - 1);
        if (error) break;
        if (data && data.length > 0) {
          results = [...results, ...data];
          if (data.length < pageSize) hasMoreData = false;
          else from += pageSize;
        } else hasMoreData = false;
        if (results.length > 50000) break;
      }
      return results;
    };

    const [allTargets, allCalls, criticalQsResult] = await Promise.all([
      fetchAllPaginatedForIds('targets', 'project_id, target_id, name', finalIds),
      fetchAllPaginatedForIds('calls', 'project_id, status, target_id, actual_start, planned_start, call_id', finalIds),
      supabase.from('mv_project_critical_questions').select('*').in('project_id', finalIds)
    ]);

    const criticalQs = criticalQsResult.data || [];
    const criticalQMap = new Map<number, any>(criticalQs.map(q => [q.project_id, q]));

    // Collect all unique question IDs across all projects to fetch answers
    const allCriticalQIds = Array.from(new Set(criticalQs.flatMap(q => [
      ...(q.complete_question_ids || []),
      ...(q.issue_question_ids || [])
    ])));

    const finalCallIds = allCalls.map(c => c.call_id);
    let allAnsData: any[] = [];

    if (allCriticalQIds.length > 0 && finalCallIds.length > 0) {
      const callIdChunks = [];
      for (let i = 0; i < finalCallIds.length; i += 1000) callIdChunks.push(finalCallIds.slice(i, i + 1000));
      const ansResults = await Promise.all(callIdChunks.map(async (callChunk) => {
        const { data } = await supabase
          .from('answers')
          .select('call_id, question_id, text, yes_no, choices')
          .in('question_id', allCriticalQIds)
          .in('call_id', callChunk);
        return data || [];
      }));
      allAnsData = ansResults.flat();
    }

    const answersByCall = new Map<number, any>();
    allAnsData.forEach(ans => {
      if (!answersByCall.has(ans.call_id)) answersByCall.set(ans.call_id, {});
      answersByCall.get(ans.call_id)[ans.question_id] = ans;
    });

    const targetsByProject = new Map<number, any[]>();
    allTargets.forEach(t => {
      if (!targetsByProject.has(t.project_id)) targetsByProject.set(t.project_id, []);
      targetsByProject.get(t.project_id)!.push(t);
    });

    const callsByTarget = new Map<string, any[]>();
    allCalls.forEach(c => {
      const tid = String(c.target_id);
      if (!callsByTarget.has(tid)) callsByTarget.set(tid, []);
      callsByTarget.get(tid)!.push(c);
    });

    return projects.map((project) => {
      const pId = project.project_id;
      const targets = targetsByProject.get(pId) || [];
      const criticalData = criticalQMap.get(pId) || {};
      const completeQIds = criticalData.complete_question_ids || [];
      const issueQIds = criticalData.issue_question_ids || [];

      let completedCount = 0;
      let fieldIssueCount = 0;
      let inProgressCount = 0;

      targets.forEach(target => {
        const tCalls = (callsByTarget.get(String(target.target_id)) || []).filter(c => c.project_id === pId);
        if (tCalls.length > 0) {
          const latestCall = tCalls.sort((a, b) => new Date(b.actual_start || b.planned_start || 0).getTime() - new Date(a.actual_start || a.planned_start || 0).getTime())[0];
          const ansMap = answersByCall.get(latestCall.call_id) || {};
          let isDone = false;
          let isIssue = false;

          const failedVisitQ = completeQIds.find((qId: number) => {
            const ans = ansMap[qId];
            const lowerVal = String(ans?.text || ans?.choices || '').toLowerCase();
            return ans?.yes_no === false || lowerVal === 'no';
          });
          const successVisitQ = completeQIds.find((qId: number) => {
            const ans = ansMap[qId];
            const lowerVal = String(ans?.text || ans?.choices || '').toLowerCase();
            return ans?.yes_no === true || lowerVal === 'yes';
          });

          if (completeQIds.length > 0) {
            if (successVisitQ !== undefined) isDone = true;
            else if (failedVisitQ !== undefined) isIssue = true;
            else if (String(latestCall.status) === '3') isDone = true;
          }
          else if (String(latestCall.status) === '3') isDone = true;

          const hasIssueReason = issueQIds.some((qId: number) => {
            const ans = ansMap[qId];
            const val = String(ans?.text || ans?.choices || '').trim().toLowerCase();
            return val && val !== 'n/a';
          });

          if (isDone) completedCount++;
          else if (isIssue || hasIssueReason) fieldIssueCount++;
          else inProgressCount++;
        }
      });

      const totalCalls = targets.length;
      let minPlanned: number | null = null, maxPlanned: number | null = null;
      targets.forEach(t => (callsByTarget.get(String(t.target_id)) || []).forEach(c => {
        if (c.planned_start) {
          const time = new Date(c.planned_start).getTime();
          if (minPlanned === null || time < minPlanned) minPlanned = time;
          if (maxPlanned === null || time > maxPlanned) maxPlanned = time;
        }
      }));

      return {
        ...project,
        stats: {
          completed: completedCount,
          totalCalls,
          issues: fieldIssueCount,
          totalPerc: totalCalls > 0 ? Math.round((completedCount / totalCalls) * 100).toString() : '0',
          minEarliest: minPlanned ? new Date(minPlanned).toISOString() : undefined,
          maxLatest: maxPlanned ? new Date(maxPlanned).toISOString() : undefined
        },
        isStatsLoading: false
      };
    });
  } catch (err) {
    console.error('Error in fetchProjectsStats:', err);
    return projects;
  }
}

// 3. Convenience wrapper for backward compatibility (optional but recommended)
export async function fetchAllProjectsSummary(userProfile?: UserProfile | null, offset = 0, limit = 50, archived = false): Promise<{ projects: ProjectSummary[], hasMore: boolean }> {
  const { projects: metadata, hasMore } = await fetchProjectsMetadata(userProfile, offset, limit, archived);
  const projectsWithStats = await fetchProjectsStats(metadata);
  return { projects: projectsWithStats, hasMore };
}

// Fast version for UI Detail View - returns metadata and basic stats immediately
export async function fetchProjectMetadata(projectId: number): Promise<Partial<ReportData>> {
  return withCache(`project_meta_${projectId}`, async () => {
    const { data: project, error } = await supabase
      .from('projects')
      .select('*')
      .eq('project_id', projectId)
      .single();

    if (error || !project) throw new Error("Project not found");

    return {
      project: {
        ...project,
        warehouse_availability: ensureArray(project.warehouse_availability),
        project_issues: ensureArray(project.project_issues)
      },
      stats: { completed: 0, inProgress: 0, issues: 0, refusal: 0, visited: 0, totalPerc: '0%', totalCalls: 0, minEarliest: null, maxLatest: null },
      storeBreakdown: [],
      detailedPhotos: [],
      isMetadataOnly: true
    };
  });
}

// Fast version for Executive Report - only fetch what's needed for the report

export async function fetchProjectData(projectId: number): Promise<ReportData> {
  console.log(`[SupabaseMock] Fetching project data for ID: ${projectId}`);
  try {
    const { data: project, error: projectError } = await supabase.from('projects').select('*').eq('project_id', projectId).single();
    if (projectError || !project) throw new Error("Project not found");

    const sanitizedProject = {
      ...project,
      warehouse_availability: ensureArray(project.warehouse_availability),
      project_issues: ensureArray(project.project_issues)
    };

    // Robust FETCH with pagination
    const fetchTablePaginated = async (table: string, pId: number) => {
      let results: any[] = [];
      const pageSize = 1000;
      let from = 0;
      while (true) {
        const { data, error } = await supabase.from(table).select('*').eq('project_id', pId).range(from, from + pageSize - 1);
        if (error || !data || data.length === 0) break;
        results = [...results, ...data];
        if (data.length < pageSize) break;
        from += pageSize;
        if (results.length > 50000) break;
      }
      return results;
    };

    // Parallelize core data fetching
    const [targets, calls, { data: questionnaires }] = await Promise.all([
      fetchTablePaginated('targets', projectId),
      fetchTablePaginated('calls', projectId),
      supabase.from('questionnaires').select('*').eq('project_id', projectId)
    ]);

    // Pre-sort calls once
    calls.sort((a, b) => {
      const d1 = new Date(a.actual_start || a.planned_start || a.created_at || 0).getTime();
      const d2 = new Date(b.actual_start || b.planned_start || b.created_at || 0).getTime();
      return d2 - d1;
    });

    const questionMap = new Map<number, { formCaption: string, questionCaption: string, type?: number }>();
    let completeQIds: number[] = [];
    let failQIds: number[] = [];
    let returnReqQIds: number[] = [];

    questionnaires?.forEach(q => {
      try {
        let formsData = typeof q.forms === 'string' ? JSON.parse(q.forms) : q.forms;
        if (typeof formsData === 'string') formsData = JSON.parse(formsData);
        if (Array.isArray(formsData)) {
          formsData.forEach((form: any) => {
            const formCaption = form.Caption?.DefaultCaption || "General Section";
            form.Questions?.forEach((ques: any) => {
              const qText = ques.Caption?.DefaultCaption || "";
              questionMap.set(ques.QuestionID, { formCaption, questionCaption: qText, type: ques.Type });
              const txt = qText.toLowerCase();
              if (txt.includes("were you able to complete this visit")) completeQIds.push(ques.QuestionID);
              if ((txt.includes("why") && txt.includes("not") && txt.includes("complete")) ||
                (txt.includes("reason") && txt.includes("return")) ||
                (txt.includes('what') && txt.includes('reason') && txt.includes('return')) ||
                (txt.includes('specify') && txt.includes('reason')) ||
                (txt.includes('damages') && txt.includes('repair'))) {
                failQIds.push(ques.QuestionID);
              }
              if (txt.includes('required') && txt.includes('return') && txt.includes('store')) returnReqQIds.push(ques.QuestionID);
            });
          });
        }
      } catch (e) { }
    });

    // Safely chunk to avoid URL length limits and paginate to avoid 1000-row max
    const callIds = calls.map(c => c.call_id);
    const callChunks = [];
    for (let i = 0; i < callIds.length; i += 100) callChunks.push(callIds.slice(i, i + 100));

    const allAnswers: any[] = [];
    const CONCURRENCY_LIMIT = 5;
    for (let i = 0; i < callChunks.length; i += CONCURRENCY_LIMIT) {
      const batch = callChunks.slice(i, i + CONCURRENCY_LIMIT);
      const batchPromises = batch.map(async (chunk) => {
        const chunkAnswers: any[] = [];
        let start = 0;
        while (true) {
          const { data, error } = await supabase.from('answers')
            .select('call_id, question_id, text, number, choices, yes_no, datetime, picture, signature')
            .in('call_id', chunk)
            .range(start, start + 999);

          if (error) { console.error('Error fetching answers:', error); break; }
          if (!data || data.length === 0) break;

          chunkAnswers.push(...data);
          if (data.length < 1000) break;
          start += 1000;
        }
        return chunkAnswers;
      });
      const results = await Promise.all(batchPromises);
      results.forEach(res => allAnswers.push(...res));
    }

    const photoAnswers = allAnswers.filter(a => a.picture !== null);

    // Map answers by call_id for O(1) access
    const answersByCall = new Map<number, any[]>();
    allAnswers.forEach(a => {
      if (!answersByCall.has(a.call_id)) answersByCall.set(a.call_id, []);
      answersByCall.get(a.call_id)!.push(a);
    });

    // Map calls by target_id for O(1) access
    const callsByTarget = new Map<string, any[]>();
    calls.forEach(c => {
      const tid = String(c.target_id);
      if (!callsByTarget.has(tid)) callsByTarget.set(tid, []);
      callsByTarget.get(tid)!.push(c);
    });

    const storeBreakdown: StoreBreakdown[] = (targets || []).map(target => {
      let targetCalls = callsByTarget.get(String(target.target_id)) || [];

      // Filter for submitted calls (status 3) and sort by latest date
      const status3Calls = targetCalls.filter(c => String(c.status) === '3');
      if (status3Calls.length > 0) {
        targetCalls = status3Calls.sort((a, b) => {
          const timeA = new Date(a.actual_start || a.planned_start || 0).getTime();
          const timeB = new Date(b.actual_start || b.planned_start || 0).getTime();
          return timeB - timeA;
        });
      } else {
        targetCalls = targetCalls.sort((a, b) => {
          const timeA = new Date(a.actual_start || a.planned_start || 0).getTime();
          const timeB = new Date(b.actual_start || b.planned_start || 0).getTime();
          return timeB - timeA;
        });
      }

      if (targetCalls.length === 0) {
        return { 
          name: target.name, 
          target_id: target.target_id,
          urgent: target.urgent,
          urgency_reason: target.urgency_reason,
          time_marked_urgernt: target.time_marked_urgernt,
          status: 'Unplanned', 
          date: 'Not Scheduled', 
          callData: null 
        };
      }

      const bestCall = targetCalls[0];
      const answers = answersByCall.get(bestCall.call_id) || [];

      const answersMapCur = new Map<number, any>();
      answers.forEach(ans => {
        const existing = answersMapCur.get(ans.question_id);
        const has = (a: any) => String(a?.text || a?.choices || a?.number || a?.yes_no || '').trim().length > 0;
        if (!existing || (!has(existing) && has(ans))) answersMapCur.set(ans.question_id, ans);
      });

      const failedVisitQ = completeQIds.find(id => {
        const ans = answersMapCur.get(id);
        return ans?.yes_no === false || String(ans?.text || '').toLowerCase() === 'no';
      });

      const returnRequiredQ = returnReqQIds.find(id => {
        const ans = answersMapCur.get(id);
        return ans?.yes_no === true || String(ans?.text || '').toLowerCase() === 'yes';
      });

      let detailedReason: string | null = null;

      // Look for the reason in the latest call first, then fall back to older calls if left blank
      for (const call of targetCalls) {
        const callAnsList = allAnswers?.filter(a => a.call_id === call.call_id) || [];
        const callAnsMap = new Map<number, any>();
        callAnsList.forEach(a => callAnsMap.set(a.question_id, a));

        let fallbackVal: string | null = null;
        const foundId = failQIds.find(id => {
          const ans = callAnsMap.get(id);
          let val = ans?.text || ans?.choices || ans?.number;

          // Clean up JSON arrays from choices
          if (Array.isArray(val)) val = val.join(', ');
          else if (typeof val === 'string' && val.trim().startsWith('[')) {
            try { val = JSON.parse(val).join(', '); } catch (e) { }
          }

          if (val && String(val).trim().length > 0) {
            const strVal = String(val).trim();
            if (strVal.toLowerCase().includes('n/a')) {
              if (!fallbackVal) fallbackVal = strVal; // Save N/A as a last resort
              return false; // keep looking for a real reason
            }
            detailedReason = strVal;
            return true;
          }
          return false;
        });

        if (!detailedReason && fallbackVal) detailedReason = fallbackVal;
        if (detailedReason) break;
      }

      // Determine completeness based on latest call
      let status: 'Completed' | 'Planned' | 'Unplanned' = 'Unplanned';
      let isIssueFinal = false;

      if (completeQIds.length > 0) {
        const successVisitQ = completeQIds.find(id => {
          const ans = answersMapCur.get(id);
          const lowerAns = String(ans?.text || ans?.choices || '').toLowerCase();
          return ans?.yes_no === true || lowerAns === 'yes';
        });
        const failedVisitQLocal = completeQIds.find(id => {
          const ans = answersMapCur.get(id);
          const lowerAns = String(ans?.text || ans?.choices || '').toLowerCase();
          return ans?.yes_no === false || lowerAns === 'no';
        });

        if (successVisitQ !== undefined) status = 'Completed';
        else if (failedVisitQLocal !== undefined) { status = 'Unplanned'; isIssueFinal = true; }
        else if (String(bestCall.status) === '3') status = 'Completed';
        else if (String(bestCall.status) === '2' || bestCall.planned_start) status = 'Planned';
      } else {
        if (String(bestCall.status) === '3') status = 'Completed';
        else if (String(bestCall.status) === '2' || bestCall.planned_start) status = 'Planned';
      }

      if (detailedReason) isIssueFinal = true;

      const dateText = bestCall.actual_start ? new Date(bestCall.actual_start).toLocaleDateString('en-GB') : (bestCall.planned_start ? new Date(bestCall.planned_start).toLocaleDateString('en-GB') : 'N/A');

      let contactName = '';
      let signatureUrl = '';

      let firstName = '';
      let lastName = '';

      targetCalls.forEach(call => {
        const callAnswers = allAnswers?.filter(a => a.call_id === call.call_id) || [];
        callAnswers.forEach((ans: any) => {
          if (ans.signature) {
            signatureUrl = `${SIGNATURE_BASE_URL}/${ans.signature}.jpg`;
          }
          const qInfo = questionMap.get(ans.question_id);
          if (qInfo && ans.text && typeof ans.text === 'string' && ans.text !== 'Yes' && ans.text !== 'No' && ans.text !== 'N/a') {
            const cap = qInfo.questionCaption.toLowerCase();

            // First name
            if (/manager/i.test(cap) && /first\s*name/i.test(cap)) {
              firstName = ans.text;
            }
            // Surname / Last name
            else if (/manager/i.test(cap) && /surname|last\s*name/i.test(cap)) {
              lastName = ans.text;
            }
            // Full name / Name & Surname
            else if (/manager/i.test(cap) && /name/i.test(cap)) {
              contactName = ans.text;
            }

            if (!ans.signature && (qInfo.type === 11 || qInfo.type === 12 || /signature/i.test(cap)) && ans.picture) {
              signatureUrl = `${SIGNATURE_BASE_URL}/${ans.picture}.jpg`;
            }
          }
        });

        if (!contactName && (firstName || lastName)) {
          contactName = [firstName, lastName].filter(Boolean).join(' ');
        }

        if (!contactName) {
          const sigAns = callAnswers.find(a => a.signature);
          if (sigAns) {
            const nameAns = callAnswers.find(a => (a.question_id === sigAns.question_id - 1 || a.question_id === sigAns.question_id - 2) && a.text && a.text !== 'Yes' && a.text !== 'No' && a.text !== 'N/a' && !a.picture && !a.signature && a.text.length < 40 && !/shop|road|drive|street|pharmacy|dis-chem|dischem|spar/i.test(a.text));
            if (nameAns) contactName = nameAns.text;
          }
        }
      });

      return {
        name: target.name,
        target_id: target.target_id,
        urgent: target.urgent,
        urgency_reason: target.urgency_reason,
        time_marked_urgernt: target.time_marked_urgernt,
        status,
        date: dateText,
        callData: {
          ...bestCall,
          allCalls: targetCalls,
          issue_text: detailedReason || (isIssueFinal ? 'No specific reason provided' : null),
          contactName,
          signatureUrl
        }
      };
    });

    const completedCount = storeBreakdown.filter(s => s.status === 'Completed').length;
    const totalStores = targets?.length || 0;
    const totalPerc = totalStores > 0 ? Math.round((completedCount / totalStores) * 100) : 0;

    // photoAnswers already defined above from combined fetch

    const mediaGroups = new Map<number, Map<string, any>>();
    const imageBaseUrl = IMAGE_BASE_URL;


    const detailedPhotos: DetailedPhoto[] = [];
    const rawPhotos: any[] = [];

    photoAnswers?.forEach(ans => {
      const qInfo = questionMap.get(ans.question_id);
      if (!qInfo) return;

      const callObj = (calls || []).find(c => c.call_id === ans.call_id);
      if (!callObj) return;
      const targetObj = (targets || []).find(t => String(t.target_id) === String(callObj.target_id));
      if (!targetObj) return;

      const pictureUrl = `${imageBaseUrl}/${ans.picture}.jpg`;
      rawPhotos.push({
        storeName: targetObj.name,
        questionCaption: qInfo.questionCaption,
        formCaption: qInfo.formCaption,
        photoUrl: pictureUrl,
        gpsCoordinates: callObj.position,
        date: callObj.actual_start || callObj.planned_start
      });

      if (/(front\s*of\s*store|front\s*store|outside\s*store|store\s*front)/i.test(qInfo.questionCaption)) return;

      if (!mediaGroups.has(ans.call_id)) mediaGroups.set(ans.call_id, new Map());
      const formsInCall = mediaGroups.get(ans.call_id)!;

      if (!formsInCall.has(qInfo.formCaption)) {
        formsInCall.set(qInfo.formCaption, {
          before: null, after: null, date: callObj.actual_start || callObj.planned_start, storeName: targetObj.name, gps: callObj.position
        });
      }

      const entry = formsInCall.get(qInfo.formCaption)!;
      const qCap = qInfo.questionCaption.toLowerCase();
      const isBefore = /(before|pre-|prework|prior|start|initial)/i.test(qCap);
      const isAfter = /(after|post-|postwork|finish|completion|done|end|final)/i.test(qCap);

      if (isBefore && !entry.before) entry.before = pictureUrl;
      else if (isAfter && !entry.after) entry.after = pictureUrl;
      else if (!entry.before) entry.before = pictureUrl;
      else if (!entry.after) entry.after = pictureUrl;
    });

    mediaGroups.forEach((forms) => {
      forms.forEach((entry, formName) => {
        if (entry.before || entry.after) {
          detailedPhotos.push({
            date: entry.date, timestamp: entry.date, storeName: `${entry.storeName} - ${formName}`, formCaption: formName,
            beforePhoto: entry.before || '', afterPhoto: entry.after || '', gpsCoordinates: entry.gps
          });
        }
      });
    });

    const allPlannedStart = (calls || []).filter(c => c.planned_start).map(c => new Date(c.planned_start).getTime());
    const minEarliest = allPlannedStart.length > 0 ? new Date(Math.min(...allPlannedStart)).toISOString() : null;
    const maxLatest = allPlannedStart.length > 0 ? new Date(Math.max(...allPlannedStart)).toISOString() : null;

    return {
      project: sanitizedProject,
      stats: {
        completed: completedCount,
        inProgress: storeBreakdown.filter(s => s.status === 'In Progress' || s.status === 'Planned').length,
        issues: storeBreakdown.filter(s => s.status === 'Issue').length || (project.project_issues?.filter((i: any) => i.status === 'open').length || 0),
        refusal: 0,
        visited: new Set(calls?.map(c => c.target_id)).size,
        totalPerc: `${totalPerc}%`,
        totalCalls: totalStores,
        minEarliest,
        maxLatest
      },
      completedStores: storeBreakdown.filter(s => s.status === 'Completed').map(s => s.name),
      inProgressStores: [],
      refusalStores: [],
      issueStores: [],
      notes: [],
      storeBreakdown,
      detailedPhotos,
      rawPhotos
    };
  } catch (err) {
    console.error('[SupabaseMock] Error fetching project data:', err);
    throw err;
  }
}

export async function updateProjectIssues(projectId: number, issues: ProjectIssue[]) {
  return await supabase.from('projects').update({ project_issues: issues }).eq('project_id', projectId);
}

export async function updateProjectWarehouseAvailability(projectId: number, availability: WarehouseAvailability[]) {
  return await supabase.from('projects').update({ warehouse_availability: availability }).eq('project_id', projectId);
}

export async function updateProjectDetails(projectId: number, updates: Partial<Project>) {
  return await supabase.from('projects').update(updates).eq('project_id', projectId);
}

export async function getClientPreferences(): Promise<ClientPreference[]> {
  const { data } = await supabase.from('client_preferences').select('*');
  return data || [];
}

export async function saveClientPreference(pref: ClientPreference) {
  if (pref.id) {
    const { error } = await supabase.from('client_preferences').update(pref).eq('id', pref.id);
    return !error;
  } else {
    const { error } = await supabase.from('client_preferences').insert(pref);
    return !error;
  }
}

export async function deleteClientPreference(id: string) {
  const { error } = await supabase.from('client_preferences').delete().eq('id', id);
  return !error;
}

export async function getClientBranding(clientName: string) {
  const { data } = await supabase.from('clients').select('client_logo_url, client_cover_url').eq('name', clientName).single();
  return data || null;
}

export async function updateClientBranding(clientName: string, updates: { client_logo_url?: string, client_cover_url?: string }) {
  const { error } = await supabase.from('clients').update(updates).eq('name', clientName);
  return !error;
}

export async function updateClientPenaltyCost(clientName: string, cost: number) {
  const { error } = await supabase.from('clients').update({ non_compliant_penalty_cost: cost }).eq('name', clientName);
  return !error;
}

export async function updateUserLinkedClients(userId: string, clients: string[]) {
  const { error } = await supabase.from('profiles').update({ linked_clients: clients }).eq('id', userId);
  return !error;
}

export async function updateUserProfile(userId: string, updates: Partial<UserProfile>) {
  const { error } = await supabase.from('profiles').update(updates).eq('id', userId);
  return !error;
}

export async function registerNewUser(email: string, pass: string, fullName: string, role: string, linkedClients: string[]) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password: pass,
    options: {
      data: {
        full_name: fullName,
        role: role,
        linked_clients: linkedClients
      }
    }
  });

  if (!error && data?.user) {
    // Manually update the profile to ensure full_name and linked_clients are set,
    // since the standard trigger might only handle ID and Email.
    await updateUserProfile(data.user.id, {
      full_name: fullName,
      role: role,
      linked_clients: linkedClients,
      status: 'active'
    });
  }

  return { data, error };
}

export async function createExportJob(userId: string, projectId: number, projectName: string) {
  const { data, error } = await supabase.from('data_exports').insert({
    user_id: userId,
    project_id: projectId,
    project_name: projectName,
    status: 'pending'
  }).select('id');

  if (error) {
    console.error('Supabase createExportJob error:', error);
    return { id: null, error };
  }

  const jobId = data && data.length > 0 ? data[0].id : null;
  return { id: jobId, error: null };
}

export async function getExportJobStatus(jobId: string) {
  const { data, error } = await supabase.from('data_exports').select('status, file_url, error_message').eq('id', jobId).single();
  if (error) return { status: 'failed', fileUrl: null, error: error.message };
  return { ...data, fileUrl: data.file_url, error: data.error_message };
}